import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2,
  Search,
  ExternalLink,
  ImageIcon,
  Film,
  Type,
  Calendar,
  Globe,
  Eye,
  Play,
  X,
  Building2,
  Trash2,
  Plus,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/google-ads-transparency';
const RADAR_API = 'https://api.glowytics.com/api/google-ads-transparency-radar';
const DEBOUNCE = 400;
const MIN_Q = 2;

/* ── Brand ID helper ─────────────────────────────────────────────── */

const getBrandId = () => {
  try { const c = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId'); if (c) return c; } catch (_) {}
  for (const k of ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id']) { try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {} }
  for (const k of ['selectedBrand', 'currentBrand', 'activeBrand']) { try { const r = localStorage.getItem(k) || sessionStorage.getItem(k); if (r) { const o = JSON.parse(r); if (o?.id) return o.id; } } catch (_) {} }
  try { const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/); if (m) return m[0]; } catch (_) {}
  return '';
};

/* ── API ────────────────────────────────────────────────────────────── */

async function postJson(path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const brandId = getBrandId();
  if (brandId) headers['X-Brand-ID'] = brandId;
  const res = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(body) });
  const text = await res.text();
  let json; try { json = JSON.parse(text); } catch { json = { _raw: text }; }
  return { ok: res.ok, status: res.status, json };
}

async function searchAdvertisers(q, country, limit, signal) {
  const res = await fetch(`${API}/advertisers/search?${new URLSearchParams({ q, country, limit: String(limit) })}`, { credentials: 'include', signal });
  const text = await res.text();
  let json; try { json = JSON.parse(text); } catch { json = { _raw: text }; }
  return { ok: res.ok, json };
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

const cid = (c) => c.ad_creative_id || c.creative_id || c.id || '';

// Google creative CDN images (tpc.googlesyndication.com, i.ytimg.com, …) fail to
// render in bulk directly from the browser (per-host connection cap + hotlink
// handling). Route them through the backend media-proxy, which fetches reliably
// and disk-caches. Non-Google/relative URLs pass through untouched.
const PROXY_HOST_RE = /(?:googlesyndication|ytimg|youtube|ggpht|googleusercontent|doubleclick|2mdn|gstatic)\.com|2mdn\.net/i;
const proxied = (u) => {
  if (!u || u === 'N/A' || !/^https:\/\//i.test(u) || !PROXY_HOST_RE.test(u)) return u || '';
  return `${RADAR_API}/media-proxy?url=${encodeURIComponent(u)}`;
};
const imgUrl = (c) => proxied(c.image || c.image_url || c.creative_url || c.thumbnail || '');
const vidUrl = (c) => proxied(c.link || c.video_url || c.video_link || '');
const detLink = (c) => c.details_link || c.details_url || '';
const domain = (c) => c.target_domain || c.domain || '';
const na = (v) => (!v || v === 'N/A') ? '' : v;
const fmt = (c) => {
  const f = (c.format || c.ad_format || c.ad_format_type || '').toLowerCase();
  if (f.includes('video')) return 'video';
  if (f.includes('image')) return 'image';
  if (f.includes('text')) return 'text';
  if (vidUrl(c)) return 'video';
  if (imgUrl(c)) return 'image';
  return 'text';
};
const fmtDate = (v) => {
  if (!v || v === 0) return '';
  if (typeof v === 'number') { const d = new Date(v > 1e12 ? v : v * 1000); return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
  if (typeof v === 'string') { const d = new Date(v); return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
  return '';
};
const fmtRegDate = (v) => {
  if (!v) return '-';
  const s = String(v);
  if (s.length === 8) { const dt = new Date(+s.slice(0,4), +s.slice(4,6)-1, +s.slice(6,8)); if (!isNaN(dt.getTime())) return dt.toLocaleDateString('en-US', { day:'numeric', month:'short', year:'numeric' }); }
  return fmtDate(v) || '-';
};
const fmtSyncDate = (v) => {
  if (!v) return 'Never';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  const now = new Date();
  const diffMs = now - d;
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};
const fmtDateLabel = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

const GoogleAdsIcon = ({ className = 'w-7 h-7' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
    <path d="M15.5 8.5L10 15.5l-2.5-2" />
  </svg>
);

/* ── Format badge ──────────────────────────────────────────────────── */

function FormatBadge({ format }) {
  const m = {
    video: 'bg-purple-50 text-purple-700 border-purple-200',
    image: 'bg-blue-50 text-blue-700 border-blue-200',
    text: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const labels = { video: 'Video', image: 'Image', text: 'Text' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold border ${m[format] || m.text}`}>
      {labels[format] || 'Text'}
    </span>
  );
}

/* ── Ad Card ───────────────────────────────────────────────────────── */

function AdCard({ creative, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const [vidErr, setVidErr] = useState(false);

  const f = fmt(creative);
  const img = imgUrl(creative);
  const vid = vidUrl(creative);
  const d = domain(creative);
  const w = creative.width;
  const h = creative.height;
  const hasVisual = (f === 'image' || f === 'video') && (img || vid);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden cursor-pointer hover:bg-slate-50/60 transition-colors group"
      onClick={onClick}>
      {/* Visual */}
      {hasVisual && (
        <div className="relative bg-slate-100 overflow-hidden" style={{ aspectRatio: w && h ? `${w}/${h}` : '16/10', maxHeight: 260 }}>
          {f === 'video' && vid && !vidErr ? (
            <div className="w-full h-full relative">
              <video src={vid} className="w-full h-full object-contain" muted playsInline preload="metadata"
                onError={() => setVidErr(true)}
                onMouseEnter={(e) => { try { e.target.play(); } catch {} }}
                onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch {} }} />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                <div className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                </div>
              </div>
            </div>
          ) : img && !imgErr ? (
            <img src={img} alt="" className="w-full h-full object-contain"
              loading="lazy" referrerPolicy="no-referrer" onError={() => setImgErr(true)} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-200"><ImageIcon className="w-10 h-10" /></div>
          )}
        </div>
      )}

      {/* Text ad fallback */}
      {!hasVisual && (
        <div className="px-4 py-5 bg-slate-50 min-h-[80px] flex flex-col justify-center">
          {d && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mb-1"><Globe className="w-3 h-3" />{d}</span>}
          <div className="flex items-center gap-2 text-slate-300">
            <Type className="w-4 h-4 shrink-0" />
            <span className="text-xs text-slate-400">Text ad</span>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-t border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <FormatBadge format={f} />
          {d && hasVisual && <span className="text-[10px] text-slate-400 truncate">{d}</span>}
          {w > 0 && h > 0 && <span className="text-[10px] text-slate-300">{w}&times;{h}</span>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {creative.total_days_shown > 0 && <span className="text-[10px] text-slate-400">{creative.total_days_shown}d</span>}
          {(creative.first_shown || creative.last_shown) && (
            <span className="text-[10px] text-slate-400">{fmtDate(creative.first_shown) || ''}{creative.last_shown && creative.first_shown ? ' - ' : ''}{fmtDate(creative.last_shown) || ''}</span>
          )}
          {detLink(creative) && (
            <a href={detLink(creative)} target="_blank" rel="noopener noreferrer"
              className="p-1 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors"
              onClick={(e) => e.stopPropagation()}>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Detail Popup (centered modal) ─────────────────────────────────── */

function DetailPopup({ creative, result, loading, onClose }) {
  const [imgErr, setImgErr] = useState(false);
  if (!result && !loading) return null;

  const img = creative ? imgUrl(creative) : '';
  const vid = creative ? vidUrl(creative) : '';
  const isVideo = creative && fmt(creative) === 'video';
  const si = result?.search_information || {};
  const dc = (result?.ad_creatives || [])[0] || {};
  const headline = na(dc.headline || dc.title);
  const snippet = na(dc.snippet || dc.text || dc.ad_text || dc.description);
  const visLink = na(dc.visible_link);
  const logo = dc.advertiser_logo || '';
  const fundedBy = si.ad_funded_by || '';
  const regions = si.regions || [];
  const lastShown = si.last_shown;
  const moreUrl = si.more_ads_by_advertiser || '';
  const f = si.format || (creative ? fmt(creative) : '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{ animation: 'popIn 0.2s ease-out' }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-900">Ad Preview</span>
            {f && f !== 'unknown' && <FormatBadge format={f} />}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading...</span>
          </div>
        )}

        {result && !loading && (
          <div className="overflow-y-auto flex-1">
            {/* Visual */}
            {(img || vid) && !imgErr && (
              <div className="bg-slate-100 flex items-center justify-center" style={{ maxHeight: 400 }}>
                {isVideo && vid ? (
                  <video src={vid} className="max-w-full max-h-[400px] object-contain" controls muted playsInline preload="metadata" autoPlay />
                ) : img && img !== 'N/A' ? (
                  <img src={img} alt="" className="max-w-full max-h-[400px] object-contain" referrerPolicy="no-referrer" onError={() => setImgErr(true)} />
                ) : null}
              </div>
            )}

            <div className="p-5 space-y-4">
              {/* Advertiser */}
              {(fundedBy || dc.title) && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  {logo && <img src={logo} alt="" className="w-10 h-10 rounded-lg object-contain border border-slate-200 bg-white" referrerPolicy="no-referrer" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{dc.title || fundedBy}</p>
                    {fundedBy && dc.title && fundedBy !== dc.title && <p className="text-[11px] text-slate-500">Funded by: {fundedBy}</p>}
                  </div>
                  {moreUrl && (
                    <a href={moreUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-sky-600 hover:underline font-medium flex items-center gap-1 shrink-0">
                      All ads <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}

              {/* Ad copy */}
              {(headline || snippet || visLink) && (
                <div className="p-3 rounded-xl border border-slate-100 bg-white space-y-1">
                  {headline && <h3 className="text-sm font-bold text-slate-900">{headline}</h3>}
                  {snippet && <p className="text-xs text-slate-600 leading-relaxed">{snippet}</p>}
                  {visLink && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><Globe className="w-3 h-3" />{visLink}</span>}
                </div>
              )}

              {/* Regions */}
              {regions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-0.5 h-4 rounded-full bg-slate-300" />
                    <span className="text-[13px] font-semibold text-slate-800 tracking-tight">Regions ({regions.length})</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 overflow-hidden max-h-[180px] overflow-y-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Region</th>
                          <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">First</th>
                          <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Last</th>
                        </tr>
                      </thead>
                      <tbody>
                        {regions.map((r, i) => (
                          <tr key={r.region || i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                            <td className="px-3 py-1.5 text-sm font-medium text-slate-700">{r.region_name || r.region}</td>
                            <td className="px-3 py-1.5 text-sm text-slate-500 tabular-nums">{fmtRegDate(r.first_shown)}</td>
                            <td className="px-3 py-1.5 text-sm text-slate-500 tabular-nums">{fmtRegDate(r.last_shown)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {lastShown > 0 && (
                <p className="text-[11px] text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Last shown: {fmtDate(lastShown)}</p>
              )}
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes popIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════════════ */

export default function GoogleAdsTransparencyRapidLab() {
  /* ── Detail popup state ─────────────────────────────────────────── */
  const [detailCreative, setDetailCreative] = useState(null);
  const [detailResult, setDetailResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailCacheRef = useRef(new Map());
  const detailReqRef = useRef(0);

  /* ── Radar state ───────────────────────────────────────────────── */
  const [savedAdvertisers, setSavedAdvertisers] = useState([]);
  const [selectedRadarId, setSelectedRadarId] = useState(null);
  const [radarCreatives, setRadarCreatives] = useState([]);
  const [radarLoading, setRadarLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState('');
  const [addHits, setAddHits] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addingToRadar, setAddingToRadar] = useState(false);
  const addAbortRef = useRef(null);
  const addGenRef = useRef(0);
  const [deleteState, setDeleteState] = useState({ open: false, account: null });
  const [filterQuery, setFilterQuery] = useState('');
  const radarGridRef = useRef(null);

  /* ── Date filter state ─────────────────────────────────────────── */
  const [radarDates, setRadarDates] = useState([]);       // available fetched_date list (desc)
  const [radarSelectedDate, setRadarSelectedDate] = useState(null); // currently selected date string
  const [radarRange, setRadarRange] = useState({ min: null, max: null }); // active broadcast range (first/last shown)

  /* ── Filter: only image/video ──────────────────────────────────── */
  const radarCreativesFiltered = radarCreatives.filter(c => fmt(c) === 'image' || fmt(c) === 'video');

  /* ── Load saved advertisers on mount ─────────────────────────────── */
  const loadSavedAdvertisers = useCallback(async () => {
    const brandId = getBrandId();
    if (!brandId) return;
    try {
      const resp = await fetch(`${RADAR_API}/advertisers`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) return;
      const data = await resp.json();
      setSavedAdvertisers(data?.advertisers || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadSavedAdvertisers(); }, [loadSavedAdvertisers]);

  const filteredSaved = savedAdvertisers.filter(a =>
    (a.advertiser_name || '').toLowerCase().includes(filterQuery.toLowerCase()) ||
    (a.advertiser_id || '').toLowerCase().includes(filterQuery.toLowerCase())
  );

  /* ── Load creatives for a specific date ────────────────────────── */
  const loadCreativesForDate = useCallback(async (advId, date) => {
    const brandId = getBrandId();
    if (!brandId || !advId) return;
    setRadarLoading(true);
    setRadarCreatives([]);
    try {
      const qs = date ? `?date=${date}` : '';
      const resp = await fetch(`${RADAR_API}/advertiser/${advId}/creatives${qs}`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const items = (data?.creatives || []).map(c => c.raw_json ? { ...c.raw_json, _db: c } : c);
        setRadarCreatives(items);
      }
    } catch (_) {}
    finally { setRadarLoading(false); }
  }, []);

  /* ── Select saved advertiser → load dates + creatives ──────────── */
  const selectRadarAdvertiser = useCallback(async (adv) => {
    const newId = selectedRadarId === adv.id ? null : adv.id;

    if (!newId) { setSelectedRadarId(null); setRadarCreatives([]); setRadarDates([]); setRadarSelectedDate(null); return; }
    setSelectedRadarId(newId);
    setRadarLoading(true);
    setRadarCreatives([]);
    setRadarDates([]);
    setRadarSelectedDate(null);

    const brandId = getBrandId();
    try {
      // Fetch available dates
      const datesResp = await fetch(`${RADAR_API}/advertiser/${adv.id}/dates`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      let dates = [];
      let range = { min: null, max: null };
      if (datesResp.ok) {
        const datesData = await datesResp.json();
        dates = datesData?.dates || [];
        range = datesData?.range || { min: null, max: null };
      }
      setRadarDates(dates);
      setRadarRange(range);

      // Default to NO date filter so the grid shows ALL archived creatives
      // (latest snapshot per creative). Only ~10 creatives per sync get their
      // real broadcast window enriched (RapidAPI enrich_limit cap), so defaulting
      // to "today" would filter the grid down to those few dated ads and hide the
      // dozens of undated-but-valid creatives. The date picker below stays
      // available as an opt-in narrowing (Google-Transparency-style window filter).
      const dateStr = null;
      setRadarSelectedDate(dateStr);

      const qs = dateStr ? `?date=${dateStr}` : '';
      const resp = await fetch(`${RADAR_API}/advertiser/${adv.id}/creatives${qs}`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const items = (data?.creatives || []).map(c => c.raw_json ? { ...c.raw_json, _db: c } : c);
        setRadarCreatives(items);
      }
    } catch (_) {}
    finally {
      setRadarLoading(false);
      setTimeout(() => radarGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [selectedRadarId]);

  /* ── Date change handler ───────────────────────────────────────── */
  const handleDateChange = useCallback((dateStr) => {
    if (!selectedRadarId || dateStr === radarSelectedDate) return;
    setRadarSelectedDate(dateStr);
    loadCreativesForDate(selectedRadarId, dateStr);
  }, [selectedRadarId, radarSelectedDate, loadCreativesForDate]);

  /* ── Add advertiser to radar (save only) ───────────────────────── */
  const addToRadar = useCallback(async (advertiserInput, advertiserName) => {
    const brandId = getBrandId();
    if (!brandId) { toast.error('Brand not selected'); return; }
    setAddingToRadar(true);
    setAddError('');
    try {
      const resp = await fetch(`${RADAR_API}/advertiser/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({ advertiserInput, advertiserName, brandId }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || 'Failed to add advertiser');
      toast.success(`${data.advertiser?.advertiser_name || 'Advertiser'} saved`);
      await loadSavedAdvertisers();
      setAddInput(''); setAddHits([]); setIsAddOpen(false);
    } catch (e) {
      const msg = e?.message || 'Failed to add';
      toast.error(msg);
      setAddError(msg);
    } finally { setAddingToRadar(false); }
  }, [loadSavedAdvertisers]);

  /* ── Debounced search inside Add modal ───────────────────────────── */
  useEffect(() => {
    if (!isAddOpen) return;
    const q = addInput.trim();
    if (q.length < MIN_Q) { addAbortRef.current?.abort(); addGenRef.current++; setAddHits([]); setAddSearching(false); return; }
    setAddSearching(true);
    const t = setTimeout(async () => {
      addAbortRef.current?.abort(); const ac = new AbortController(); addAbortRef.current = ac; const g = ++addGenRef.current;
      try {
        const { ok, json } = await searchAdvertisers(q, 'TR', 15, ac.signal);
        if (g !== addGenRef.current) return;
        setAddHits(ok && Array.isArray(json.items) ? json.items : []);
      } catch (e) { if (e.name === 'AbortError') return; if (g === addGenRef.current) setAddHits([]); }
      finally { if (g === addGenRef.current) setAddSearching(false); }
    }, DEBOUNCE);
    return () => { clearTimeout(t); addAbortRef.current?.abort(); };
  }, [addInput, isAddOpen]);

  /* ── Delete advertiser from radar ────────────────────────────────── */
  const performDelete = useCallback(async () => {
    if (!deleteState?.account) return;
    const brandId = getBrandId();
    if (!brandId) return;
    try {
      const resp = await fetch(`${RADAR_API}/advertiser/${deleteState.account.id}`, {
        method: 'DELETE',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');
      setSavedAdvertisers(prev => prev.filter(a => a.id !== deleteState.account.id));
      if (selectedRadarId === deleteState.account.id) { setSelectedRadarId(null); setRadarCreatives([]); setRadarDates([]); setRadarSelectedDate(null); }
      toast.success('Advertiser removed');
      setDeleteState({ open: false, account: null });
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  }, [deleteState, selectedRadarId]);

  /* ── Manual sync ─────────────────────────────────────────────────── */
  const syncAdvertiser = useCallback(async (adv) => {
    const brandId = getBrandId();
    if (!brandId) return;
    setSyncingId(adv.id);
    try {
      const resp = await fetch(`${RADAR_API}/advertiser/${adv.id}/sync`, {
        method: 'POST',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      const data = await resp.json();
      if (resp.ok && data?.ok) {
        toast.success(`${adv.advertiser_name}: ${data.stored} creatives synced`);
        await loadSavedAdvertisers();
        if (selectedRadarId === adv.id) selectRadarAdvertiser(adv);
      } else {
        toast.error(data?.error || 'Sync failed');
      }
    } catch (e) { toast.error(e?.message || 'Sync failed'); }
    finally { setSyncingId(null); }
  }, [selectedRadarId, selectRadarAdvertiser, loadSavedAdvertisers]);

  const closeDetail = useCallback(() => { setDetailCreative(null); setDetailResult(null); setDetailLoading(false); }, []);

  const selectedRadarAdv = savedAdvertisers.find(a => a.id === selectedRadarId);

  return (
    <div className="p-6 bg-slate-50/50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* ═══ Saved Advertisers Table ═══ */}
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white mb-5">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/googlelogopng.png" alt="Google" className="h-7" />
              <span className="text-base font-semibold text-slate-900">Ads Transparency</span>
            </div>
            <div className="flex items-center gap-2">
              {savedAdvertisers.length > 3 && (
                <input value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} placeholder="Filter..."
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors w-40" />
              )}
              <button className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                onClick={() => { setIsAddOpen(true); setAddError(''); setAddInput(''); }}>
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </div>

          {/* Saved advertisers list */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '40%' }}>Advertiser</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Creatives</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Region</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Last Sync</th>
                  <th className="px-4 py-2.5" style={{ width: '15%' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSaved.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                    {savedAdvertisers.length === 0 ? 'No advertisers tracked yet. Click "+ Add" to start.' : 'No match.'}
                  </td></tr>
                )}
                {filteredSaved.map((adv) => {
                  const isSelected = selectedRadarId === adv.id;
                  return (
                    <tr key={adv.id}
                      className={`cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${isSelected ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}`}
                      onClick={() => selectRadarAdvertiser(adv)}>
                      <td className="px-4 py-2.5 text-sm align-middle">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-semibold shrink-0">
                            {(adv.advertiser_name || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate text-slate-900">{adv.advertiser_name || '(No name)'}</div>
                            <div className="text-[10px] text-slate-400 font-mono">{adv.advertiser_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">
                        {Number(adv.total_creatives || 0).toLocaleString('en-US')}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-600">{adv.region || 'TR'}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-500">{fmtSyncDate(adv.last_synced_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="inline-flex p-1.5 rounded hover:bg-blue-50 text-slate-300 hover:text-blue-500 transition-colors"
                            title="Sync now"
                            onClick={(e) => { e.stopPropagation(); syncAdvertiser(adv); }}>
                            <RefreshCw className={`w-3.5 h-3.5 ${syncingId === adv.id ? 'animate-spin' : ''}`} />
                          </button>
                          <button className="inline-flex p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                            title="Remove"
                            onClick={(e) => { e.stopPropagation(); setDeleteState({ open: true, account: adv }); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ Ad Grid — from radar (DB) ═══ */}
        {selectedRadarId && (
          <div ref={radarGridRef}>
            {/* Radar header bar */}
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold">
                  {(selectedRadarAdv?.advertiser_name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{selectedRadarAdv?.advertiser_name}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{selectedRadarAdv?.advertiser_id}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Date filter — picks ads whose real broadcast window covers the chosen day */}
                {radarRange.min && radarRange.max && (
                  <div className="relative" title={`Yayın aralığı: ${radarRange.min} – ${radarRange.max}`}>
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      value={radarSelectedDate || ''}
                      min={radarRange.min}
                      max={radarRange.max}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="pl-8 pr-2 py-1.5 text-[12px] font-medium border border-slate-200 rounded-lg bg-slate-50 hover:bg-white focus:bg-white focus:border-slate-300 outline-none transition-colors cursor-pointer text-slate-700"
                    />
                  </div>
                )}

                {radarCreativesFiltered.length > 0 && (
                  <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {radarCreativesFiltered.length} ads
                  </span>
                )}
                <button
                  className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                  disabled={syncingId === selectedRadarId}
                  onClick={() => selectedRadarAdv && syncAdvertiser(selectedRadarAdv)}>
                  <RefreshCw className={`w-3 h-3 ${syncingId === selectedRadarId ? 'animate-spin' : ''}`} />
                  Sync Now
                </button>
                <button onClick={() => { setSelectedRadarId(null); setRadarCreatives([]); setRadarDates([]); setRadarSelectedDate(null); }}
                  className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {radarLoading && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center gap-2 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading archived creatives...</span>
              </div>
            )}

            {!radarLoading && radarCreativesFiltered.length > 0 && (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {radarCreativesFiltered.map((c, i) => (
                  <AdCard key={`${cid(c)}-${i}`} creative={c} onClick={() => {
                    const extId = selectedRadarAdv?.advertiser_id;
                    if (!extId || !c.ad_creative_id) return;
                    const cacheKey = `${extId}:${c.ad_creative_id}`;
                    const cached = detailCacheRef.current.get(cacheKey);
                    if (cached) {
                      setDetailCreative(c); setDetailResult(cached); setDetailLoading(false);
                      return;
                    }
                    const reqId = ++detailReqRef.current;
                    setDetailCreative(c); setDetailLoading(true); setDetailResult(null);
                    postJson('/ad-details', { advertiser_id: String(extId), creative_id: String(c.ad_creative_id), details_url: detLink(c) || '', region: 'anywhere', max_regions: 25 })
                      .then(({ ok, json }) => {
                        if (reqId !== detailReqRef.current) return;
                        if (ok && json) detailCacheRef.current.set(cacheKey, json);
                        setDetailResult(json);
                        if (!ok) toast.error(json?.error || 'Detail failed');
                      })
                      .catch(e => { if (reqId !== detailReqRef.current) return; toast.error(e.message); setDetailResult({ error: e.message }); })
                      .finally(() => { if (reqId === detailReqRef.current) setDetailLoading(false); });
                  }} />
                ))}
              </div>
            )}

            {!radarLoading && radarCreativesFiltered.length === 0 && radarCreatives.length === 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                <p className="text-sm text-slate-400">No creatives archived yet. Click "Sync Now" to fetch ads.</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ═══ Add Advertiser Modal (search + dropdown) ═══ */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setIsAddOpen(false); setAddHits([]); setAddInput(''); }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            style={{ animation: 'popIn 0.2s ease-out' }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900">Add Advertiser to Radar</span>
              <button onClick={() => { setIsAddOpen(false); setAddHits([]); setAddInput(''); }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Search advertiser</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
                    value={addInput}
                    onChange={(e) => { setAddInput(e.target.value); setAddError(''); }}
                    placeholder="Type advertiser name..."
                    autoFocus
                  />
                  {addSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
                </div>

                {/* Search results dropdown */}
                {addInput.trim().length >= MIN_Q && (
                  <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden max-h-[280px] overflow-y-auto">
                    {addSearching && addHits.length === 0 && (
                      <div className="flex items-center gap-2 px-4 py-4 text-sm text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...</div>
                    )}
                    {!addSearching && addHits.length === 0 && addInput.trim().length >= MIN_Q && (
                      <div className="px-4 py-4 text-sm text-slate-400 text-center">No advertisers found for "{addInput.trim()}"</div>
                    )}
                    {addHits.map((row) => {
                      const alreadySaved = savedAdvertisers.some(a => a.advertiser_id === String(row.advertiser_id));
                      return (
                        <button key={String(row.advertiser_id)} type="button"
                          disabled={addingToRadar || alreadySaved}
                          onClick={() => { if (!alreadySaved) addToRadar(String(row.advertiser_id), row.advertiser_disclosed_name || null); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-slate-50 last:border-0 transition-colors ${alreadySaved ? 'bg-slate-50 cursor-default' : 'hover:bg-blue-50 cursor-pointer'}`}>
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <Building2 className="w-4 h-4 text-slate-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-slate-900 block truncate">{row.advertiser_disclosed_name || '(No name)'}</span>
                            <span className="text-[10px] text-slate-400 font-mono">{row.advertiser_id}</span>
                          </div>
                          {alreadySaved ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0">Saved</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 shrink-0 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {addError && <p className="text-xs text-red-500">{addError}</p>}
              {addingToRadar && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </div>
              )}
            </div>
          </div>
          <style>{`@keyframes popIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }`}</style>
        </div>
      )}

      {/* ═══ Delete Confirmation Modal ═══ */}
      {deleteState.open && deleteState.account && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDeleteState({ open: false, account: null })}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden"
            style={{ animation: 'popIn 0.2s ease-out' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Remove advertiser?</p>
              <p className="text-sm text-slate-600">
                <strong>{deleteState.account.advertiser_name}</strong> will be removed from your radar. Archived creatives will remain in the database.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  onClick={() => setDeleteState({ open: false, account: null })}>Cancel</button>
                <button className="px-4 py-1.5 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
                  onClick={performDelete}>Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail popup */}
      {(detailCreative || detailLoading) && (
        <DetailPopup creative={detailCreative} result={detailResult} loading={detailLoading} onClose={closeDetail} />
      )}
    </div>
  );
}
